import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase, IS_MOCK } from "@/lib/supabaseClient";
import { can, ROLE_LABEL } from "@/lib/permissions";

const SbAuthContext = createContext(null);
const LS_REST = "sb_current_restaurant";

export function SupabaseAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [currentRestaurantId, setCurrentRestaurantId] = useState(() => localStorage.getItem(LS_REST) || null);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadContext = useCallback(async (u) => {
    if (!u) { setProfile(null); setMemberships([]); return; }
    const { data: prof } = await supabase.from("profiles").select("*").eq("id", u.id).single();
    setProfile(prof);
    const { data: mems } = await supabase.from("memberships").select(
      "id, role, department, organization:organizations(id,name), restaurant:restaurants(id,name)"
    ).eq("profile_id", u.id);
    const list = mems || [];
    setMemberships(list);
    setCurrentRestaurantId((prev) => {
      if (prev && list.some((m) => m.restaurant?.id === prev)) return prev;
      return list.length === 1 ? list[0].restaurant?.id : prev;
    });
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!active) return;
      setSession(session);
      setUser(session?.user || null);
      await loadContext(session?.user || null);
      setLoading(false);
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user || null);
      if (event === "SIGNED_OUT") { setProfile(null); setMemberships([]); setCurrentRestaurantId(null); localStorage.removeItem(LS_REST); }
      else if (session) loadContext(session.user);
      if (event === "PASSWORD_RECOVERY") setPasswordRecovery(true);
      if (event === "USER_UPDATED") setPasswordRecovery(false);
    });
    return () => { active = false; subscription.unsubscribe(); };
  }, [loadContext]);

  const currentMembership = memberships.find((m) => m.restaurant?.id === currentRestaurantId) || null;
  const role = currentMembership?.role || null;

const AUTH_ERR = { "Invalid login credentials": "Fel e-post eller lösenord", "Email not confirmed": "E-posten är inte bekräftad ännu" };
  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email: email.toLowerCase(), password });
    return error ? { ok: false, error: AUTH_ERR[error.message] || error.message } : { ok: true };
  };
  const signUp = async ({ email, password, fullName }) => {
    const { error } = await supabase.auth.signUp({ email: email.toLowerCase(), password, options: { data: { full_name: fullName } } });
    return error ? { ok: false, error: error.message } : { ok: true };
  };
  const signOut = async () => { await supabase.auth.signOut(); };
  const requestPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.toLowerCase(), { redirectTo: `${window.location.origin}/konto/aterstall` });
    return error ? { ok: false, error: error.message } : { ok: true };
  };
  const updatePassword = async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    return error ? { ok: false, error: error.message } : { ok: true };
  };
  const selectRestaurant = (id) => {
    if (!memberships.some((m) => m.restaurant?.id === id)) return; // only allow restaurants the user belongs to
    setCurrentRestaurantId(id);
    localStorage.setItem(LS_REST, id);
  };
  const hasPermission = (perm) => can(role, perm);

  const organizations = [...new Map(memberships.map((m) => [m.organization?.id, m.organization])).values()].filter(Boolean);
  const restaurants = memberships.map((m) => m.restaurant).filter(Boolean);

  const value = {
    session, user, profile, memberships, organizations, restaurants,
    currentMembership, currentRestaurant: currentMembership?.restaurant || null,
    currentOrganization: currentMembership?.organization || null,
    role, roleLabel: role ? ROLE_LABEL[role] : null,
    loading, passwordRecovery, isMock: IS_MOCK,
    signIn, signUp, signOut, requestPasswordReset, updatePassword,
    selectRestaurant, hasPermission,
  };
  return <SbAuthContext.Provider value={value}>{children}</SbAuthContext.Provider>;
}

export const useSbAuth = () => {
  const ctx = useContext(SbAuthContext);
  if (!ctx) throw new Error("useSbAuth must be used within SupabaseAuthProvider");
  return ctx;
};
